import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import * as api from '../../services/api';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  padding: 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  padding: 40px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  text-align: center;
  margin: 0 0 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-sizing: border-box;
  margin-bottom: 16px;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59,130,246,0.2);
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  background: #3b82f6;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 16px;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorText = styled.div`
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 12px;
`;

const SuccessText = styled.div`
  color: #16a34a;
  font-size: 13px;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const BackLink = styled(Link)`
  display: block;
  text-align: center;
  font-size: 14px;
  color: #6b7280;
  margin-top: 4px;
  text-decoration: none;
  &:hover { color: #374151; }
`;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <Wrapper>
        <Card>
          <Title>Invalid Link</Title>
          <ErrorText>This password reset link is invalid or has expired.</ErrorText>
          <BackLink to="/forgot-password">Request a new link</BackLink>
        </Card>
      </Wrapper>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.confirmPasswordReset(token, password);
      if (res?.success) {
        setDone(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(res?.message || 'Reset failed. The link may have expired.');
      }
    } catch (err: any) {
      setError(err?.data?.message || err?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Card>
        <Title>Set New Password</Title>
        {done ? (
          <>
            <SuccessText>Password updated. Redirecting to sign in...</SuccessText>
            <BackLink to="/login">Sign in now</BackLink>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <ErrorText>{error}</ErrorText>}
            <Label htmlFor="password">New password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Min 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              placeholder="Repeat new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
            <SubmitButton type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Set Password'}
            </SubmitButton>
            <BackLink to="/login">Back to sign in</BackLink>
          </form>
        )}
      </Card>
    </Wrapper>
  );
};

export default ResetPassword;
